import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown } from 'lucide-react';
import type { LockedSummary } from '../../services/search';

interface LockedResultsCardProps {
  locked: LockedSummary;
  historyMonths: number;
}

function formatAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
}

export default function LockedResultsCard({ locked, historyMonths }: LockedResultsCardProps) {
  const { t, i18n } = useTranslation('search');
  const navigate = useNavigate();
  const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Faux résultats floutés */}
      <div className="relative">
        <div className="opacity-40 pointer-events-none select-none p-4 space-y-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 py-2 blur-sm">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
              <div className="h-3 w-24 bg-gray-200 rounded" />
              <div className="h-3 w-20 bg-gray-200 rounded" />
              <div className="flex-1" />
              <div className="h-3 w-16 bg-gray-200 rounded" />
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/70 to-white/95">
          <div className="flex flex-col items-center gap-3 p-6 text-center max-w-md">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">
              {t('locked.title', { count: locked.count })}
            </h3>
            <p className="text-sm text-gray-600">
              {t('locked.message', {
                count: locked.count,
                historyMonths,
                credits: formatAmount(locked.creditsSum, locale),
                debits: formatAmount(locked.debitsSum, locale),
              })}
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard/abonnement')}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <Crown className="w-4 h-4" />
              {t('locked.upgrade')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
